import './index.less';
import { cloneDeep, debounce, isNumber, max, min } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import CurPenSvg from '@/assets/cur-pen.svg';
import {
    BaseStagePlugin,
    checkPointInStage,
    checkPointInViewport,
    combineOriginIndex,
    convertToLayerPoint,
    convertToStagePoint,
    getFramework,
    getStageRect,
    MoveListenerLevel,
    replaceOriginIndex,
    StageScaleView,
    trackConfig,
    useActiveOriginIndex,
    useActiveStageIndex,
    useIdentity,
    useMoveEventListener,
} from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import {
    clampFillWidth,
    clampFontSize,
    computedSegmentBaseInfo,
    getDefaultMarkupPoint,
    getMarkupDefaultLayer,
    LabelKit,
    MARKUP_POINT_SIZE,
    MarkupLayer,
    MarkupLayerView,
    updateScreenshotUrl,
} from '@/layers/markup-layer';
import { isHotKeyPress, ITouchKitEvent } from '@/utils/event-kit';
import { getNewCenterPoint, setLayerRuntimeValue } from '@/utils/layer-kit';
import { getActualAngle, getPointsBetweenAngle, subPoint, translatePointAngle } from '@/utils/math-kit';
import {
    BaseLayerViewType,
    DirectionType,
    IExportPlugin,
    ILayerProps,
    IMarkupAnchor,
    IMarkupDrawConfig,
    IMarkupLayerProps,
    IMarkupPoint,
    IPoint,
    LayerType,
    MarkupType,
    MaterialIdsType,
    PluginType,
    ResizeOperateType,
} from '@/version-types';
import { DRAW_EVENTS } from './event-name';
import { MarkupDrawPlugin } from './markup-draw';
import markupDrawPluginState, { getMarkupLayerState, useMarkupLayerState } from './states/markup-state';

/**
 * 适配标注笔的基础信息
 *
 * @param identity - ID
 * @param markupData - 标记数据
 * @param scale - 缩放比例
 * @param stageIndex - 可选；舞台序号；默认值为 `0`
 */
const adapterDrawMarkupBaseInfo = (identity: string, markupData: IMarkupDrawConfig, scale: number, stageIndex = 0) => {
    // 获取舞台大小的画布
    function getFullCanvas(info: any) {
        const { width, height } = getStageRect(identity, stageIndex);
        info.width = width / scale;
        info.height = height / scale;
        info.left = 0;
        info.top = 0;
        info.naturalWidth = info.width;
        info.naturalHeight = info.height;
        return info;
    }

    return {
        /** 箭头笔 */
        [MarkupType.Arrow](info) {
            // 计算直线的旋转角度
            function getLineTransform(info: any) {
                const { maxY, maxX, top: minY, left: minX, x1, x2, y1, y2 } = info;
                const centerPoint = {
                    x: (x2 - x1) / 2 + x1,
                    y: (y2 - y1) / 2 + y1,
                };
                const start = {
                    x: x2 - x1,
                    y: y2 - y1,
                };
                const rotate = -getPointsBetweenAngle(start, { x: 1, y: 0 });
                const line = Math.pow(maxY - minY, 2) + Math.pow(maxX - minX, 2);
                return {
                    centerPoint,
                    rotate: getActualAngle(rotate),
                    width: +Math.sqrt(line).toFixed(0),
                };
            }
            // 获取直线的基础信息
            function getLineBaseInfo(info, height = 0, minWidth = 0) {
                const { centerPoint, rotate, width } = getLineTransform(info);
                info.width = Math.max(width, minWidth);
                info.height = height;
                info.top = centerPoint.y - info.height / 2;
                info.left = centerPoint.x - info.width / 2;
                info.rotate = rotate;
                info.centerOffset = {
                    x: 0,
                    y: Math.max(markupData.anchor.arrowLeft[0], markupData.anchor.arrowRight[0]),
                };
                return info;
            }

            /**
             * 获取箭头的最小尺寸
             *
             * @param fillWidth - 填充宽度
             * @param startPoint - 开始坐标点
             * @param endPoint - 结束坐标点
             */
            function getArrowMinSize(
                fillWidth: number,
                startPoint: IMarkupPoint,
                endPoint: IMarkupPoint,
                anchor: IMarkupAnchor,
            ) {
                startPoint = startPoint || getDefaultMarkupPoint();
                endPoint = endPoint || getDefaultMarkupPoint();
                // 计算新的宽高，素材规范为 4px 大小
                const startPointHeight = (startPoint.height / MARKUP_POINT_SIZE) * fillWidth;
                const endPointHeight = (endPoint.height / MARKUP_POINT_SIZE) * fillWidth;
                const minHeight = Math.max(
                    startPointHeight,
                    endPointHeight,
                    fillWidth,
                    anchor.arrowLeft[0] - anchor.arrowLeft[1],
                    anchor.arrowRight[0] - anchor.arrowRight[1],
                );
                const startPointWidth = (startPoint.width / MARKUP_POINT_SIZE) * fillWidth;
                const endPointWidth = (endPoint.width / MARKUP_POINT_SIZE) * fillWidth;
                const minWidth = Math.max(startPointWidth, endPointWidth);
                return {
                    minHeight,
                    minWidth,
                };
            }

            const { minHeight, minWidth } = getArrowMinSize(
                markupData.fill.width,
                markupData.startPoint,
                markupData.endPoint,
                markupData.anchor,
            );

            return getLineBaseInfo(info, minHeight, minWidth);
        },

        [MarkupType.Segment](info) {
            getFullCanvas(info);
            return info;
        },
    };
};

/** 获取绘制的基础信息 */
function getDrawMarkupBaseInfo(
    identity: string,
    markupData: IMarkupDrawConfig,
    startPoint: IPoint,
    endPoint: IPoint,
    scale: number,
    stageIndex = 0,
) {
    const { x: x1, y: y1 } = startPoint;
    const { x: x2, y: y2 } = endPoint;
    const maxX = max([x1, x2]) as number;
    const minX = min([x1, x2]) as number;
    const maxY = max([y1, y2]) as number;
    const minY = min([y1, y2]) as number;
    const info = {
        width: maxX - minX,
        height: maxY - minY,
        left: minX,
        top: minY,
        rotate: 0,
        maxX,
        maxY,
        x1,
        x2,
        y1,
        y2,
        naturalWidth: maxX - minX,
        naturalHeight: maxY - minY,
        centerOffset: { x: 0, y: 0 },
    };

    // 重新适配基础信息
    adapterDrawMarkupBaseInfo(identity, markupData, scale, stageIndex)[markupData.markupType](info);

    return info;
}

const DrawOperate: React.FC = React.memo(() => {
    // const mask = useRef<HTMLDivElement>(null);
    const identity = useIdentity();
    const activeStageIndex = useActiveStageIndex();
    const activeOriginIndex = useActiveOriginIndex();
    const framework = getFramework(identity);
    // const isInit = useRef(false);
    /** 绘制的鼠标点 */
    const mousePoints = useRef<IPoint[]>([]);
    /** 绘制时的预览图层 */
    const preview = useRef<ILayerProps>();
    /** 绘制时的预览图层 */
    const result = useRef<ILayerProps>();
    /** 画笔属性面板 */
    const drawConfig = useMarkupLayerState()((state) => state.drawConfig);
    /** 渲染预览图层数据 */
    const [previewLayer, setPreviewLayer] = useState<ILayerProps | null>(null);
    /** 自定义鼠标样式 */
    const [penPoint, setPenPoint] = useState<IPoint | null>(null);
    /** 第几次点击 */
    const clickCount = useRef(0);
    /** 计算label大小 */
    const labelKit = useRef<LabelKit>();
    /** 画笔实时属性 */
    const [markupData, setDrawMarkupData] = useState<IMarkupDrawConfig | null>(null);
    /** 是否正在绘制 */
    const drawing = useRef('');
    /** 绘制操作类型 */
    const drawOperateType = {
        click: 'click',
        mouse: 'mouse',
    };
    const { $t } = useLocaleStore();
    const drawTips = {
        tip: $t((v) => v.brushLayer.tips.moveToCanvas),
        [MarkupType.Arrow]: {
            0: $t((v) => v.markupLayer.tips.firstClick),
            1: $t((v) => v.markupLayer.tips.secondClick),
            2: $t((v) => v.markupLayer.tips.thirdClick),
        },
        [MarkupType.Segment]: {
            0: $t((v) => v.markupLayer.tips.firstClick),
        },
    };

    const currentWidth = framework.getSizeByIndex(activeStageIndex).width;
    /** 根据舞台大小，等比例缩放配置 */
    useEffect(() => {
        if (drawConfig) {
            const data = cloneDeep(drawConfig);
            const ratio = currentWidth / 800;
            data.label.fontSize = clampFontSize(data.label.fontSize * ratio);
            data.fill.width = clampFillWidth(data.fill.width * ratio);
            data.anchor.arrowLeft[0] = Math.ceil(data.anchor.arrowLeft[0] * ratio);
            data.anchor.arrowLeft[1] = Math.ceil(data.anchor.arrowLeft[1] * ratio);
            data.anchor.arrowRight[0] = Math.ceil(data.anchor.arrowRight[0] * ratio);
            data.anchor.arrowRight[1] = Math.ceil(data.anchor.arrowRight[1] * ratio);
            setDrawMarkupData(data);
            createLabelKit();
        }
        return () => {
            labelKit.current?.destroy();
        };
    }, [currentWidth, drawConfig]);

    function createPreviewLayer() {
        preview.current = getMarkupDefaultLayer(markupData!, true);
        if (preview.current) {
            preview.current.id = combineOriginIndex(preview.current.id, framework.stage.getActiveOriginIndex());
        }
        framework.createFontFamilySubset([preview.current], false).then(() => {
            console.log('createFontFamilySubset', preview.current);
        });
    }

    // 创建绘制预览图层
    useEffect(() => {
        if (markupData) {
            createPreviewLayer();
        }
    }, [markupData]);

    // 替换绘制预览图层id
    useEffect(() => {
        if (preview.current) {
            preview.current.id = replaceOriginIndex(preview.current.id, framework.stage.getActiveOriginIndex());
        }
    }, [activeStageIndex]);

    function clearDrawConfig() {
        getMarkupLayerState(identity).setDrawConfig(null);
    }

    useEffect(() => {
        if (!drawConfig) return;
        const handle = framework.events.onEsc(() => {
            clearDrawConfig();
        });
        return () => {
            framework.events.off(handle);
        };
    }, [drawConfig]);

    // 操纵绘制
    useEffect(() => {
        if (!markupData) return;
        // 禁用图层选中事件，取消激活图层
        framework.operate.disablePick();
        framework.operate.delHoverId();
        framework.stage.setIsPickStage(false);

        const drawType = drawOperateType.click;
        // 绑定点击事件
        const clickHandle = (e) => {
            if (!markupData) return;
            switch (clickCount.current) {
                case 0:
                    if (!drawing.current) {
                        firstClick(e, drawType);
                        showDrawTip(e);
                    }
                    break;
                case 1:
                    if (drawing.current === drawType) {
                        secondClick(e);
                        showDrawTip(e);
                        drawPreview(e);
                        convertPreviewPropsToLayer();
                    }
                    break;
                case 2:
                    if (drawing.current === drawType) {
                        thirdClick(e);
                        showDrawTip(e);
                        drawPreview(e);
                        convertPreviewPropsToLayer();
                    }
                    break;
            }
            // showDrawTip(e);
        };

        const mouseEnterHandler = framework.events.onMouseEnter((originIndex) => {
            const isDrawing = clickCount.current !== 0;
            if (!isDrawing && framework.stage.isLongImageMode()) {
                framework.stage.setActiveStageIndex(framework.convertOriginToStage(originIndex));
            }
        });

        // 绑定绘制事件
        const mouseMoveHandle = debounce(
            (e) => {
                if (!markupData) return;
                if (drawing.current === drawType) {
                    drawPreview(e);
                }
                const mPoint = { x: e.x, y: e.y };
                const isDrawing = clickCount.current !== 0;
                const stageIndex = framework.stage.getActiveStageIndex();
                const isInStage = checkPointInStage(identity, mPoint, stageIndex);
                if (isDrawing || isInStage) {
                    setPenPoint(mPoint);
                } else {
                    if (!isDrawing) {
                        setPenPoint(null);
                    }
                }
                showDrawTip(e);
            },
            5,
            { leading: true },
        );
        const mouseDownHandle = (e) => {
            showDrawTip(e, true);
        };
        document.addEventListener('click', clickHandle);
        document.addEventListener('mousedown', mouseDownHandle);
        document.addEventListener('mousemove', mouseMoveHandle);
        return () => {
            framework.operate.cursorTip.clear();
            framework.operate.enablePick();
            framework.events.off(mouseEnterHandler);
            document.removeEventListener('click', clickHandle);
            document.removeEventListener('mousedown', mouseDownHandle);
            document.removeEventListener('mousemove', mouseMoveHandle);
        };
    }, [markupData]);

    useMoveEventListener(
        (e: ITouchKitEvent) => {
            if (!markupData) return;
            const drawType = drawOperateType.mouse;
            if (e.isStart && !drawing.current) {
                const stageIndex = framework.stage.getActiveStageIndex();
                const startInStage = checkPointInStage(identity, e.startPoint, stageIndex);
                if (startInStage) {
                    firstClick(e, drawType);
                }
            }
            if (drawing.current === drawType) {
                drawPreview(e);
            }
            if (e.isEnd && drawing.current === drawType) {
                secondClick(e);
                drawPreview(e);
                convertPreviewPropsToLayer();
                setTimeout(() => {
                    drawing.current = drawOperateType.click;
                }, 10);
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'markup-draw-default',
    );

    // 绘制提示
    function showDrawTip(e: IPoint, needDestroy = false) {
        if (!markupData) return;
        const mPoint = { x: e.x, y: e.y };
        if (!checkPointInViewport(identity, mPoint)) {
            framework.operate.cursorTip.clear();
            return;
        }
        const stageIndex = framework.stage.getActiveStageIndex();
        const isInStage = checkPointInStage(identity, mPoint, stageIndex);
        const isDrawing = clickCount.current !== 0;
        if (!isInStage && !isDrawing) {
            if (needDestroy) {
                clearDrawConfig();
            }
            framework.operate.cursorTip.set({
                type: 'custom',
                x: mPoint.x,
                y: mPoint.y,
                txt: drawTips.tip,
            });
        } else {
            const tip = drawTips[markupData.markupType]?.[clickCount.current];
            if (tip) {
                framework.operate.cursorTip.set({
                    type: 'custom',
                    x: mPoint.x,
                    y: mPoint.y,
                    txt: tip,
                });
            } else {
                framework.operate.cursorTip.clear();
            }
        }
    }

    // 第一次点击
    function firstClick(e, type: string) {
        const stageIndex = framework.stage.getActiveStageIndex();
        const inStage = checkPointInStage(identity, e, stageIndex);
        if (!inStage) return false;
        drawing.current = type;
        clickCount.current = 0;
        mousePoints.current.length = 0;
        result.current = undefined;
        clickCount.current += 1;
        const point = convertToLayerPoint(identity, e, stageIndex);
        mousePoints.current.push(point);
        if (!preview.current) {
            createPreviewLayer();
        }
        drawPreview(e);
        return true;
    }
    // 第二次点击
    function secondClick(e) {
        if (!preview.current) return;
        const stageIndex = framework.stage.getActiveStageIndex();
        clickCount.current += 1;
        const point = convertToLayerPoint(identity, e, stageIndex);
        mousePoints.current.push(point);
    }
    // 第三次点击
    function thirdClick(e) {
        if (!preview.current) return;
        clickCount.current += 1;
    }

    function fixLinePoint(point: IPoint) {
        // 绘制 90 180 的角度
        const hotKeyPress = isHotKeyPress();
        if (hotKeyPress) {
            const firstPoint = mousePoints.current[0];
            const xLength = Math.abs(point.x - firstPoint.x);
            const yLength = Math.abs(point.y - firstPoint.y);
            if (xLength > yLength) {
                point.y = firstPoint.y;
            } else {
                point.x = firstPoint.x;
            }
        }
    }

    /** 执行绘制 */
    function drawPreview(e) {
        if (preview.current && markupData) {
            const previewProps = preview.current;
            const stageIndex = framework.stage.getActiveStageIndex();
            const currentPoint = convertToLayerPoint(identity, e, stageIndex);
            const scale = framework.stage.getScale();

            if (clickCount.current === 1) {
                fixLinePoint(currentPoint);
                // 第2个点的绘制
                const info = getDrawMarkupBaseInfo(
                    identity,
                    markupData,
                    mousePoints.current[0],
                    currentPoint,
                    scale,
                    stageIndex,
                );
                // console.log(info);
                Object.assign(previewProps, {
                    width: ~~info.width,
                    height: ~~info.height,
                    left: ~~info.left,
                    top: ~~info.top,
                    rotate: info.rotate,
                });

                // 实时调整label的大小
                if (markupData.markupType === MarkupType.Arrow) {
                    Object.assign(previewProps.data, {
                        centerOffset: { ...info.centerOffset },
                        // naturalWidth: info.naturalWidth,
                        // naturalHeight: info.naturalHeight,
                    });

                    const res = adjustDrawArrowTowOperate(previewProps.width);
                    // if (res && markupData.label.background) {
                    //     res.roundWeight = res?.height || 0;
                    // }
                    Object.assign((previewProps.data as IMarkupLayerProps).label, res);
                }

                // 实时调整绘制点坐标
                if (markupData.markupType === MarkupType.Segment) {
                    const pointStart = subPoint(mousePoints.current[0], currentPoint);
                    Object.assign(previewProps.data, {
                        centerOffset: currentPoint,
                        anchor: {
                            points: [pointStart],
                        },
                    });
                    // 计算label
                    createLabelKit();
                    const tempLayer = getMarkupDefaultLayer(markupData, true);
                    (tempLayer.data as IMarkupLayerProps).label = markupData.label;
                    const res = labelKit.current?.getSize(tempLayer);
                    if (res) {
                        Object.assign((previewProps.data as IMarkupLayerProps).label, res);
                    }
                }
                (previewProps.data as IMarkupLayerProps).label.roundWeight = LabelKit.getRoundWeight(previewProps);
                setPreviewLayer(cloneDeep(previewProps));
            }

            if (clickCount.current === 2 || (!result.current && clickCount.current === markupData?.__drawClick)) {
                if (markupData.markupType === MarkupType.Arrow) {
                    result.current = adjustDrawArrowThreeOperate(
                        cloneDeep(previewProps),
                        previewProps,
                        mousePoints.current[1],
                        currentPoint,
                        scale,
                    );
                    setPreviewLayer(result.current);
                }

                // 实时调整绘制点坐标
                if (markupData.markupType === MarkupType.Segment) {
                    const data = previewProps.data as IMarkupLayerProps;
                    const pointEnd = subPoint(currentPoint, data.centerOffset);
                    Object.assign(previewProps.data, {
                        anchor: {
                            points: [data.anchor.points![0], pointEnd],
                        },
                    });
                    result.current = cloneDeep(previewProps);
                    setPreviewLayer(result.current);
                }
            }
        }
    }
    /** 箭头标注，调整第2个操作 label 大小 */
    function adjustDrawArrowTowOperate(width: number) {
        if (!markupData) return;
        createLabelKit();

        const layer = getMarkupDefaultLayer(markupData, true);
        (layer.data as IMarkupLayerProps).label = markupData.label;
        const res = labelKit.current?.getFontSize(layer, markupData.label.fontSize, width);

        return {
            fontSize: res?.fontSize || markupData.label.fontSize,
            width: res?.width || width,
            height: res?.height || markupData.label.height,
            roundWeight: 0,
        };
    }
    /** 箭头标注，第3个操作图层大小 */
    function adjustDrawArrowThreeOperate(
        newLayer: ILayerProps,
        originLayer: ILayerProps,
        startPoint: IPoint,
        currentPoint: IPoint,
        scale: number,
    ) {
        startPoint = translatePointAngle(startPoint, -newLayer.rotate);
        const endPoint = translatePointAngle(currentPoint, -newLayer.rotate);
        const operateType = startPoint.y <= endPoint.y ? ResizeOperateType.BC : ResizeOperateType.TC;
        const direction = operateType === ResizeOperateType.TC ? DirectionType.OPPOSITE : DirectionType.POSITIVE;
        const isTC = operateType === ResizeOperateType.TC;
        const top = endPoint.y - startPoint.y;
        const offset = Math.floor(top * direction);
        newLayer.height = offset + originLayer.height;
        const newCenter = getNewCenterPoint(originLayer as any, newLayer, isTC, 1);
        newLayer.left = newCenter.x - newLayer.width / 2;
        newLayer.top = newCenter.y - newLayer.height / 2;

        const newLayerData = newLayer.data as IMarkupLayerProps;
        const originData = originLayer.data as IMarkupLayerProps;
        if (isTC) {
            newLayerData.anchor.arrowLeft[1] = Math.min(
                originData.anchor.arrowLeft[1] - offset,
                originData.anchor.arrowLeft[1],
            );
            newLayerData.anchor.arrowRight[1] = Math.min(
                originData.anchor.arrowRight[1] - offset,
                originData.anchor.arrowRight[1],
            );
        } else {
            newLayerData.anchor.arrowLeft[0] = Math.max(
                originData.anchor.arrowLeft[0] + offset,
                originData.anchor.arrowLeft[0],
            );
            newLayerData.anchor.arrowRight[0] = Math.max(
                originData.anchor.arrowRight[0] + offset,
                originData.anchor.arrowRight[0],
            );
            newLayerData.centerOffset.y = Math.max(newLayerData.anchor.arrowLeft[0], newLayerData.anchor.arrowRight[0]);
        }

        return newLayer;
    }
    /**
     * 修正尺寸图绘制最终层宽高，定位
     *
     * @param layer - 图层属性
     */
    function fixDrawArrowResult(layer: ILayerProps) {
        const data = layer.data as IMarkupLayerProps;
        const {
            fill,
            anchor: { arrowLeft, arrowRight },
            startPoint,
            endPoint,
        } = data;
        const {
            renderArea: { up, down },
        } = LabelKit.getLabelRenderInfo(layer.width, fill.width, layer);
        const leftPointHeight = startPoint ? (startPoint.height / MARKUP_POINT_SIZE) * fill.width : 0;
        const rightPointHeight = endPoint ? (endPoint.height / MARKUP_POINT_SIZE) * fill.width : 0;
        const halfPointHeight = Math.ceil(Math.max(leftPointHeight / 2, rightPointHeight / 2));
        const upOrigin = Math.max(arrowLeft[0], arrowRight[0], halfPointHeight);
        const upResult = Math.max(up, upOrigin);
        const downOrigin = Math.min(arrowLeft[1], arrowRight[1], -halfPointHeight);
        const downResult = Math.min(down, downOrigin);
        // console.log(upResult, upOrigin, upResult - upOrigin);
        if (upResult > upOrigin) {
            const offset = upResult - upOrigin;
            // console.log(offset);

            const { left, top, width, height, rotate } = layer;
            layer.height += offset;
            const newCenter = getNewCenterPoint(
                { left, top, width, height, rotate },
                { width, height: layer.height },
                true,
                1,
            );
            layer.left = newCenter.x - width / 2;
            layer.top = newCenter.y - layer.height / 2;
            data.centerOffset.y = upResult;
        }
        if (downResult < downOrigin) {
            const offset = downOrigin - downResult;
            const { left, top, width, height, rotate } = layer;
            layer.height += offset;
            const newCenter = getNewCenterPoint(
                { left, top, width, height, rotate },
                { width, height: layer.height },
                false,
                1,
            );
            layer.left = newCenter.x - width / 2;
            layer.top = newCenter.y - layer.height / 2;
        }
    }
    /**
     * 修正指引线图绘制最终层宽高，定位
     *
     * @param layer - 图层属性
     */
    function fixDrawSegmentResult(layer: ILayerProps) {
        const baseInfo = computedSegmentBaseInfo(layer);
        Object.assign(layer, baseInfo);
    }

    /**
     * 转换成真实图层
     */
    function convertPreviewPropsToLayer() {
        let layer = result.current;
        if (!layer) return;
        const data = layer.data as IMarkupLayerProps;
        const markupType = data.markupType;
        if (isNumber(markupData?.__drawClick) && markupData.__drawClick > clickCount.current) return;
        delete layer.__runtime?.isDrawPreview;
        layer = cloneDeep(layer);
        if (markupType === MarkupType.Arrow) {
            fixDrawArrowResult(layer);
        }
        if (markupType === MarkupType.Segment) {
            fixDrawSegmentResult(layer);
        }
        if (markupData?.__vipFuncType) {
            if (!layer.materialIds) {
                layer.materialIds = {};
            }
            const funcList = layer.materialIds[MaterialIdsType.FUNC_LIST] || [];
            funcList.push(markupData?.__vipFuncType);
            layer.materialIds[MaterialIdsType.FUNC_LIST] = funcList;
        }
        setLayerRuntimeValue(layer, {
            isInit: true,
        });

        // console.log(cloneDeep(layer));
        const resLayer = new MarkupLayer(identity).add({
            layer,
            trackFunc: trackConfig[LayerType.BRUSH_LAYER][`add_${markupType}`],
        });
        // 绘制结束
        updateScreenshotUrl(identity, resLayer.id);
        framework.events.emit(DRAW_EVENTS.DRAW_END, markupType);
        clearDrawConfig();
    }

    /**
     * 创建文字工具
     */
    function createLabelKit() {
        if (!labelKit.current) {
            labelKit.current = new LabelKit();
        }
    }

    if (!penPoint || !markupData) return null;

    const stagePoint = convertToStagePoint(identity, penPoint, activeStageIndex);

    return (
        <BaseStagePlugin originIndexOrLayerId={activeOriginIndex} name="markup-draw">
            <RenderPen x={stagePoint.x} y={stagePoint.y} />
            {previewLayer ? (
                <StageScaleView originIndex={activeOriginIndex}>
                    <MarkupLayerView layer={previewLayer} identity={identity} isNavigator={false} />
                </StageScaleView>
            ) : null}
        </BaseStagePlugin>
    );
});

const RenderPen: React.FC<IPoint> = React.memo(({ x, y }) => {
    return (
        <div className="markup-draw-mask" data-operate-type={BaseLayerViewType.MASK}>
            <div className="markup-draw-mask-pen" style={{ transform: `translate(${x}px, ${y}px)` }}>
                <img src={CurPenSvg} width={16} height={16} />
            </div>
        </div>
    );
});

const DrawPluginView: React.FC = React.memo(() => {
    const drawConfig = useMarkupLayerState()((state) => state.drawConfig);
    const identity = useIdentity();
    const isInit = useRef(false);
    const framework = getFramework(identity);

    useEffect(() => {
        if (!isInit.current) {
            isInit.current = true;
            return;
        }
        framework.events.emit(DRAW_EVENTS.CONFIG_CHANGE, drawConfig);
    }, [drawConfig]);
    if (!drawConfig) return null;
    return <DrawOperate />;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 9,
    name: 'markup-draw',
    render() {
        return <DrawPluginView />;
    },
    createState() {
        return [markupDrawPluginState];
    },
    createInstance({ identity }) {
        return [{ key: 'MarkupDrawPlugin', value: new MarkupDrawPlugin(identity) }];
    },
};

export default exportPlugin;
